import { useParams } from "react-router";
import api from "@/services/api";
import API from "@/services/api";
import toast from "react-hot-toast";
import { useState, useEffect, useRef } from "react";
import { useComponentTranslation } from "@/services/i18n";
import { FiCheck, FiX, FiSearch } from "react-icons/fi";
import TiptapEditor from "@/components/tiptap-editor";
import "@/styles/tiptap-editor.css";
import { countryOptions } from "@/constants/countries";


// MultiSelect Component
function MultiSelect({ options = [], value = [], onChange, placeholder, className = "" }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const hasSelection = selectedValues.length > 0;

  const filteredOptions = searchTerm.trim() === ""
    ? options
    : options.filter((option) => option.label?.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggleOption = (optionValue) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between select min-h-9 h-fit py-1.5 text-sm"
      >
        {hasSelection ? (
          <div className="flex flex-wrap gap-1">
            {value.map((v) => {
              const option = options.find((o) => o.value === v);
              if (!option) return null;
              return (
                <span key={v} className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-xs">
                  {option.label}
                </span>
              );
            })}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="absolute left-0 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50 max-h-48 overflow-auto">
          <div className="px-2 pb-1 border-b border-gray-100">
            <div className="relative">
              <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                className="w-full pl-7 pr-2 py-1 text-xs focus:outline-none"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          {filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggleOption(option.value)}
              className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50"
            >
              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selectedValues.includes(option.value) ? "bg-primary border-primary" : "border-gray-300"}`}>
                {selectedValues.includes(option.value) && <FiCheck className="w-2.5 h-2.5 text-white" />}
              </div>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const translations = {
  en: {
    update_success: "Saved",
    more_options: "More options",
    fields: {
      title: { label: "Job Title", placeholder: "e.g. Senior Software Engineer" },
      contract: {
        label: "Contract",
        placeholder: "Select type",
        types: { CDI: "Permanent", CDD: "Fixed-term", INTERIM: "Temporary", FREELANCE: "Freelance", INTERNSHIP: "Internship", SELF_EMPLOYED: "Self-employed", WORK_STUDY: "Work-study" },
      },
      employment_type: {
        label: "Employment",
        placeholder: "Select type",
        types: { FULL_TIME: "Full-time", PART_TIME: "Part-time" },
      },
      workplace_type: {
        label: "Location",
        placeholder: "Select type",
        types: { ON_SITE: "On-site", REMOTE: "Remote", HYBRID: "Hybrid" },
      },
      city: { label: "City", placeholder: "Enter city" },
      country: { label: "Country", placeholder: "Select a country" },
      languages: { label: "Languages", placeholder: "Select languages" },
      experience: { label: "Experience (years)", placeholder: "0" },
      vacancy: { label: "Positions", placeholder: "1" },
      salary_min: { label: "Min salary", placeholder: "0" },
      salary_max: { label: "Max salary", placeholder: "0" },
      currency: { label: "Currency" },
      period: { label: "Period", options: { HOUR: "Per hour", DAY: "Per day", WEEK: "Per week", MONTH: "Per month", YEAR: "Per year" } },
      start_date: { label: "Start date", placeholder: "Select date" },
      expiration_date: { label: "Expires on", placeholder: "Select date" },
      duration: { label: "Duration (months)", placeholder: "0" },
    },
    description: {
      title: "Description",
      subtitle: "Describe responsibilities, requirements, and benefits",
      placeholder: "Write the job description here...",
      ai_placeholder: "Ask AI to write or improve your description...",
      ai_button: "Generate",
      ai_error: "Error generating description",
      ai_success: "Description generated",
      ai_prompt_required: "Please enter a prompt",
      ai_actions: {
        generate: "Generate full description",
        improve: "Improve",
        concise: "Make concise",
      },
      ai_prompts: {
        generate: "Generate a complete job description based on the job title and details",
        improve: "Improve the current description to be more professional and engaging",
        concise: "Make the current description more concise while keeping key information",
      },
    },
    languages: { FR: "French", EN: "English", DE: "German", LB: "Luxembourgish", NL: "Dutch", ES: "Spanish", IT: "Italian", PT: "Portuguese" },
  },
  fr: {
    update_success: "Enregistré",
    more_options: "Plus d'options",
    fields: {
      title: { label: "Titre du poste", placeholder: "ex. Ingénieur Logiciel Senior" },
      contract: {
        label: "Contrat",
        placeholder: "Sélectionner",
        types: { CDI: "CDI", CDD: "CDD", INTERIM: "Intérim", FREELANCE: "Freelance", INTERNSHIP: "Stage", SELF_EMPLOYED: "Indépendant", WORK_STUDY: "Alternance" },
      },
      employment_type: {
        label: "Emploi",
        placeholder: "Sélectionner",
        types: { FULL_TIME: "Temps plein", PART_TIME: "Temps partiel" },
      },
      workplace_type: {
        label: "Lieu",
        placeholder: "Sélectionner",
        types: { ON_SITE: "Sur site", REMOTE: "Télétravail", HYBRID: "Hybride" },
      },
      city: { label: "Ville", placeholder: "Entrez la ville" },
      country: { label: "Pays", placeholder: "Sélectionner un pays" },
      languages: { label: "Langues", placeholder: "Sélectionner les langues" },
      experience: { label: "Expérience (années)", placeholder: "0" },
      vacancy: { label: "Postes", placeholder: "1" },
      salary_min: { label: "Salaire min", placeholder: "0" },
      salary_max: { label: "Salaire max", placeholder: "0" },
      currency: { label: "Devise" },
      period: { label: "Période", options: { HOUR: "Par heure", DAY: "Par jour", WEEK: "Par semaine", MONTH: "Par mois", YEAR: "Par an" } },
      start_date: { label: "Date de début", placeholder: "Sélectionner" },
      expiration_date: { label: "Expire le", placeholder: "Sélectionner" },
      duration: { label: "Durée (mois)", placeholder: "0" },
    },
    description: {
      title: "Description",
      subtitle: "Décrivez les responsabilités, exigences et avantages",
      placeholder: "Rédigez la description du poste ici...",
      ai_placeholder: "Demandez à l'IA d'écrire ou d'améliorer votre description...",
      ai_button: "Générer",
      ai_error: "Erreur lors de la génération",
      ai_success: "Description générée",
      ai_prompt_required: "Veuillez entrer une instruction",
      ai_actions: {
        generate: "Générer la description",
        improve: "Améliorer",
        concise: "Rendre concis",
      },
      ai_prompts: {
        generate: "Générer une description complète basée sur le titre et les détails du poste",
        improve: "Améliorer la description actuelle pour la rendre plus professionnelle et engageante",
        concise: "Rendre la description actuelle plus concise tout en gardant les informations clés",
      },
    },
    languages: { FR: "Français", EN: "Anglais", DE: "Allemand", LB: "Luxembourgeois", NL: "Néerlandais", ES: "Espagnol", IT: "Italien", PT: "Portugais" },
  },
};

export default function Edit({ job, setJob }) {
  const { id } = useParams();
  const [values, setValues] = useState({});
  const { t, i18n } = useComponentTranslation(translations);
  
  // AI state
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (job) {
      const formattedData = {
        ...job,
        start_date: job.start_date ? new Date(job.start_date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
        expiration_date: job.expiration_date ? new Date(job.expiration_date).toISOString().split("T")[0] : "",
      };
      setValues(formattedData);
    }
  }, [job]);

  const handleBlurSave = async (field, value) => {
    if (job[field] === value) return;
    try {
      const response = await api.put(`/job/${id}`, { [field]: value });
      if (!response.ok) return toast.error(response.code);
      setJob(response.data);
    } catch (e) {
      console.error(e);
      toast.error(e.code);
      setValues((prev) => ({ ...prev, [field]: job[field] }));
    }
  };

  const handleAISubmit = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t("description.ai_prompt_required"));
      return;
    }
    setGenerating(true);
    try {
      const { ok, data } = await API.post(`/job/${values._id}/generate-description`, { prompt: aiPrompt });
      if (!ok) {
        toast.error(data.message || t("description.ai_error"));
        return;
      }
      handleBlurSave("description", data.description);
      setValues((prev) => ({ ...prev, description: data.description }));
      setAiPrompt("");
      toast.success(t("description.ai_success"));
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    } finally {
      setGenerating(false);
    }
  };

  const languageOptions = Object.entries(t("languages", { returnObjects: true })).map(([value, label]) => ({ value, label }));
  const countryOpts = countryOptions(i18n.language);

  return (
    <div className="grid grid-cols-2 gap-6 pb-20 mt-8 px-8">
      {/* Left column: Info fields */}
      <div className="space-y-4">
        {/* Job Title */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {t("fields.title.label")} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={values.title || ""}
            onChange={(e) => setValues({ ...values, title: e.target.value })}
            onBlur={(e) => handleBlurSave("title", e.target.value)}
            placeholder={t("fields.title.placeholder")}
            className="input text-sm"
            required
          />
        </div>

        {/* Contract + Employment + Workplace */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              {t("fields.contract.label")} <span className="text-red-500">*</span>
            </label>
            <select
              value={values.contract || ""}
              onChange={(e) => { setValues({ ...values, contract: e.target.value }); handleBlurSave("contract", e.target.value); }}
              className="select text-sm"
              required
            >
              <option value="">{t("fields.contract.placeholder")}</option>
              {Object.entries(t("fields.contract.types", { returnObjects: true })).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              {t("fields.employment_type.label")} <span className="text-red-500">*</span>
            </label>
            <select
              value={values.employment_type || ""}
              onChange={(e) => { setValues({ ...values, employment_type: e.target.value }); handleBlurSave("employment_type", e.target.value); }}
              className="select text-sm"
              required
            >
              <option value="">{t("fields.employment_type.placeholder")}</option>
              {Object.entries(t("fields.employment_type.types", { returnObjects: true })).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              {t("fields.workplace_type.label")} <span className="text-red-500">*</span>
            </label>
            <select
              value={values.workplace_type || ""}
              onChange={(e) => { setValues({ ...values, workplace_type: e.target.value }); handleBlurSave("workplace_type", e.target.value); }}
              className="select text-sm"
              required
            >
              <option value="">{t("fields.workplace_type.placeholder")}</option>
              {Object.entries(t("fields.workplace_type.types", { returnObjects: true })).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Location */}
        {values.workplace_type !== "REMOTE" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.city.label")} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={values.city || ""}
                onChange={(e) => setValues((prev) => ({ ...prev, city: e.target.value }))}
                onBlur={(e) => handleBlurSave("city", e.target.value)}
                placeholder={t("fields.city.placeholder")}
                className="input text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.country.label")} <span className="text-red-500">*</span></label>
              <select
                className="select text-sm"
                value={values.country || ""}
                onChange={(e) => { setValues((prev) => ({ ...prev, country: e.target.value })); handleBlurSave("country", e.target.value); }}
                required
              >
                <option value="">{t("fields.country.placeholder")}</option>
                {countryOpts.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Languages + Experience + Positions */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.languages.label")}</label>
            <MultiSelect
              options={languageOptions}
              value={values.languages || []}
              onChange={(langs) => { setValues((prev) => ({ ...prev, languages: langs })); handleBlurSave("languages", langs); }}
              placeholder={t("fields.languages.placeholder")}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.experience.label")}</label>
            <input
              type="number"
              min="0"
              value={values.years_of_experience || ""}
              onChange={(e) => setValues({ ...values, years_of_experience: e.target.value })}
              onBlur={(e) => handleBlurSave("years_of_experience", e.target.value)}
              placeholder={t("fields.experience.placeholder")}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.vacancy.label")}</label>
            <input
              type="number"
              min="1"
              value={values.vacancy || 1}
              onChange={(e) => setValues({ ...values, vacancy: e.target.value })}
              onBlur={(e) => handleBlurSave("vacancy", e.target.value)}
              placeholder={t("fields.vacancy.placeholder")}
              className="input text-sm"
            />
          </div>
        </div>

        {/* Salary */}
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.salary_min.label")}</label>
            <input
              type="number"
              min="0"
              value={values.remuneration_min || ""}
              onChange={(e) => setValues((prev) => ({ ...prev, remuneration_min: parseFloat(e.target.value) || null }))}
              onBlur={() => handleBlurSave("remuneration_min", values.remuneration_min)}
              placeholder={t("fields.salary_min.placeholder")}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.salary_max.label")}</label>
            <input
              type="number"
              min="0"
              value={values.remuneration_max || ""}
              onChange={(e) => setValues((prev) => ({ ...prev, remuneration_max: parseFloat(e.target.value) || null }))}
              onBlur={() => handleBlurSave("remuneration_max", values.remuneration_max)}
              placeholder={t("fields.salary_max.placeholder")}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.currency.label")}</label>
            <select
              value={values.remuneration_currency || "EUR"}
              onChange={(e) => { setValues((prev) => ({ ...prev, remuneration_currency: e.target.value })); handleBlurSave("remuneration_currency", e.target.value); }}
              className="select text-sm"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="XOF">XOF</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.period.label")}</label>
            <select
              value={values.remuneration_period || "YEAR"}
              onChange={(e) => { setValues((prev) => ({ ...prev, remuneration_period: e.target.value })); handleBlurSave("remuneration_period", e.target.value); }}
              className="select text-sm"
            >
              {Object.entries(t("fields.period.options", { returnObjects: true })).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.start_date.label")}</label>
            <input
              type="date"
              min={new Date().toISOString().split("T")[0]}
              value={values.start_date || ""}
              onChange={(e) => setValues({ ...values, start_date: e.target.value })}
              onBlur={(e) => values.start_date && handleBlurSave("start_date", e.target.value)}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.expiration_date.label")}</label>
            <input
              type="date"
              min={values.start_date || new Date().toISOString().split("T")[0]}
              value={values.expiration_date || ""}
              onChange={(e) => setValues({ ...values, expiration_date: e.target.value })}
              onBlur={(e) => values.expiration_date && handleBlurSave("expiration_date", e.target.value)}
              className="input text-sm"
            />
          </div>
          {values.contract === "CDD" && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">{t("fields.duration.label")}</label>
              <input
                type="number"
                min="1"
                value={values.duration_in_month || ""}
                onChange={(e) => setValues({ ...values, duration_in_month: e.target.value })}
                onBlur={(e) => handleBlurSave("duration_in_month", e.target.value)}
                placeholder={t("fields.duration.placeholder")}
                className="input text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Right column: Description */}
      <div className="flex flex-col">
        <div className="mb-2">
          <h3 className="text-sm font-medium text-gray-700">{t("description.title")} <span className="text-red-500">*</span></h3>
          <p className="text-xs text-gray-400">{t("description.subtitle")}</p>
        </div>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden [&_.tiptap]:min-h-[600px] [&_.tiptap]:text-xs [&_.tiptap.is-empty]:before:text-xs [&_.tiptap.is-empty]:before:text-gray-400">
          <TiptapEditor
            value={values.description || ""}
            onChange={(content) => setValues((prev) => ({ ...prev, description: content }))}
            onBlur={() => handleBlurSave("description", values.description)}
            placeholder={t("description.placeholder")}
            aiEnabled={true}
            aiPrompt={aiPrompt}
            onAiPromptChange={setAiPrompt}
            onAiSubmit={handleAISubmit}
            aiGenerating={generating}
            aiPlaceholder={t("description.ai_placeholder")}
            aiQuickActions={[
              { label: t("description.ai_actions.generate"), onClick: () => { setAiPrompt(t("description.ai_prompts.generate")); } },
              { label: t("description.ai_actions.improve"), onClick: () => { setAiPrompt(t("description.ai_prompts.improve")); } },
              { label: t("description.ai_actions.concise"), onClick: () => { setAiPrompt(t("description.ai_prompts.concise")); } },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
